"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useApp } from "../contexts/AppContext"
import { login as authLogin } from "../services/auth.service"
import { motion } from "framer-motion"
import { BiLock, BiEnvelope, BiKey, BiLogInCircle } from "react-icons/bi"

import HeaderAlert from "../components/ui/HeaderAlert"

export default function LoginPage() {
    const [formData, setFormData] = useState({ email: "", password: "" })
    const [formErrors, setFormErrors] = useState({})
    const navigate = useNavigate()
    const { login, user, loading, success, error, clearError, clearSuccess, dispatch } = useApp()

    // Redirect if already logged in
    useEffect(() => {
        if (user) navigate("/")
    }, [user, navigate])

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData({ ...formData, [name]: value })
        if (formErrors[name]) {
            setFormErrors({ ...formErrors, [name]: undefined })
        }
    }

    const validateForm = () => {
        const newErrors = {}
        if (!formData.email.trim()) {
            newErrors.email = "L'email est obligatoire."
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Format d'email invalide."
        }
        if (!formData.password.trim()) {
            newErrors.password = "Le mot de passe est obligatoire."
        }
        setFormErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        clearError() // Clear previous errors

        if (!validateForm()) {
            return
        }

        try {
            dispatch({ type: "SET_LOADING", payload: true })
            const response = await authLogin(formData)
            // Assuming API returns { user: {...}, token: "..." }
            login(response.user, response.token)
            navigate("/") // Redirect to home or dashboard
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Erreur de connexion. Veuillez vérifier vos identifiants."
            dispatch({ type: "SET_ERROR", payload: errorMessage })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }

    return (
        <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 100, damping: 10 }}
                className="card shadow-lg border-0 rounded-3"
                style={{ maxWidth: "450px", width: "100%" }}
            >
                <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-4">
                        <motion.div
                            animate={{
                                scale: [1, 1.1, 1],
                                rotate: [0, 5, -5, 0],
                            }}
                            transition={{
                                duration: 2,
                                repeat: Number.POSITIVE_INFINITY,
                                repeatType: "reverse",
                            }}
                        >
                            <BiLock className="display-3 text-primary mb-3" />
                        </motion.div>
                        <h2 className="fw-bold text-primary">Connexion</h2>
                        <p className="text-muted">Accédez à votre espace de gestion de fiches</p>
                    </div>

                    <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">
                                Email
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <BiEnvelope />
                                </span>
                                <input
                                    type="email"
                                    className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="votre@email.com"
                                    disabled={loading}
                                />
                                {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label htmlFor="password" className="form-label">
                                Mot de passe
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <BiKey />
                                </span>
                                <input
                                    type="password"
                                    className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="********"
                                    disabled={loading}
                                />
                                {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                            </div>
                        </div>

                        <motion.button
                            type="submit"
                            className="btn btn-primary w-100 d-flex align-items-center justify-content-center py-2"
                            disabled={loading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Connexion en cours...
                                </>
                            ) : (
                                <>
                                    <BiLogInCircle className="me-1" /> Se connecter
                                </>
                            )}
                        </motion.button>
                    </form>
                </div>
            </motion.div>
        </div>
    )
}
